import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
  Community,
  Division,
  ContactAffiliation as ContactCompanyType,
  TeamMember as Contact,
  DefaultPhase,
  Phase,
  Event,
  Buyer,
  LotAppointment,
  PhaseTip,
  Document,
  LotDocument,
  Company,
  TeamMemberType,
  User,
  DefaultPhaseTip
} from '@tripointe/tripointe-cms/data-access';
import {
  mapInviteesToDropdown,
  parseGroupMembersNamesToInput
} from '@tripointe/tripointe-cms/utils';

import {
  FormValidationService,
  VALIDATION_CONFIGS,
  VALIDATION_NAME_CONFIG
} from './form-validation.service';
import { VALIDATION_PATTERNS } from './patterns';

interface ResetPasswordInitData {
  email: string;
  resetCode: string;
}

const NAME_PATTERN = VALIDATION_PATTERNS.entityName;
const NAME_LENGTH = VALIDATION_NAME_CONFIG.nameMaxLength;

@Injectable({
  providedIn: 'root'
})
export class FormCreatorService {
  constructor(private fb: FormBuilder, private formValidationService: FormValidationService) {}

  createUserForm(): FormGroup {
    return this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH),
          Validators.minLength(2)
        ]
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH),
          Validators.minLength(2)
        ]
      ],
      email: ['', [Validators.required, this.formValidationService.isValidEmail]],
      jobTitle: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.jobTitle),
          Validators.minLength(2)
        ]
      ],
      phone: ['', [Validators.required, this.formValidationService.isValidPhone]],
      workPhone: ['', [this.formValidationService.isValidPhone, Validators.required]],
      roleId: ['', Validators.required]
    });
  }

  editProfileDataForm(profileData: User): FormGroup {
    const baseUserPayload = {
      email: [profileData.email, [Validators.required, this.formValidationService.isValidEmail]],
      role: [profileData.role, []],
      homepageId: [profileData.homepage, []]
    };
    const userSpecificPayload = {
      firstName: [
        profileData.firstName,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      lastName: [
        profileData.lastName,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      jobTitle: [
        profileData.jobTitle,
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.jobTitle)]
      ],
      phone: [profileData.phone, [Validators.required, this.formValidationService.isValidPhone]],
      workPhone: [
        profileData.workPhone,
        [this.formValidationService.isValidPhone, Validators.required]
      ]
    };

    const teamMemberSpecificPayload = {
      groupName: [
        profileData?.teamMember?.groupName,
        [Validators.required, Validators.minLength(2), Validators.maxLength(NAME_LENGTH)]
      ],
      groupMembersNames: [profileData?.teamMember?.groupMembersNames, Validators.required]
    };

    return this.fb.group({
      ...baseUserPayload,
      ...(!profileData.teamMember && userSpecificPayload),
      ...(profileData.teamMember && teamMemberSpecificPayload)
    });
  }

  changePasswordForm(): FormGroup {
    return this.fb.group(
      {
        currentPassword: null,
        password: [null, [Validators.minLength(8)]],
        passwordConfirmation: [null, [Validators.minLength(8)]]
      },
      {
        validators: [
          this.formValidationService.oldAndNewPassDifferent,
          this.formValidationService.matchNewPasswords,
          this.formValidationService.checkCurrentPasswordFieldIsEntered
        ]
      }
    );
  }

  createUsersFiltersForm(): FormGroup {
    return this.fb.group({
      name: [null],
      email: [null],
      rolesIds: [null],
      divisionsIds: [null],
      communitiesIds: [null],
      jobTitle: [null]
    });
  }

  createEventsFilterForm(): FormGroup {
    return this.fb.group({
      divisionIds: [null],
      communityIds: [null],
      name: [null],
      phaseNames: [null]
    });
  }

  createDivisionForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      url: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.url)]],
      address: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        '',
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      stateId: ['', Validators.required],
      zip: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      countryId: ['', Validators.required],
      phone: ['', [this.formValidationService.isValidPhone, Validators.required]],
      contactUserId: ['', Validators.required],
      contactEmail: ['', [Validators.required, this.formValidationService.isValidEmail]],
      contactPhone: ['', [this.formValidationService.isValidPhone, Validators.required]],
      logo: [''],
      designStudioUrl: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.url)]],
      division149Photos: [false],
      division149PhotosId: [''],
      timeZone: ['', [Validators.required]]
    });
  }

  createDivisionsFiltersForm(): FormGroup {
    return this.fb.group({
      divisionIds: [null],
      city: [null],
      statesIds: [null],
      phone: [null]
    });
  }

  editDivisionForm(division: Partial<Division>, isDuplicate?: boolean): FormGroup {
    const {
      name,
      url,
      designStudioUrl,
      phone,
      address,
      zip,
      city,
      contactEmail,
      contactPhone,
      country: { id: countryId },
      state: { id: stateId },
      contactUser,
      logo,
      timeZone,
      division149PhotosId
    } = division;

    return this.fb.group({
      name: [
        isDuplicate ? `${name} Duplicate` : name,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      url: [url, [Validators.required, Validators.pattern(VALIDATION_PATTERNS.url)]],
      address: [
        address,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        city,
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      stateId: [stateId, Validators.required],
      zip: [zip, [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      countryId: [countryId, Validators.required],
      phone: [phone, [this.formValidationService.isValidPhone, Validators.required]],
      contactUserId: [contactUser ? contactUser.id : '', Validators.required],
      contactEmail: [contactEmail, [Validators.required, this.formValidationService.isValidEmail]],
      contactPhone: [contactPhone, [this.formValidationService.isValidPhone, Validators.required]],
      logo: [logo],
      designStudioUrl: [
        designStudioUrl,
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.url)]
      ],
      division149Photos: [!!division149PhotosId],
      division149PhotosId: [
        division149PhotosId,
        division149PhotosId
          ? [Validators.required, Validators.pattern(VALIDATION_PATTERNS.uuId)]
          : []
      ],
      timeZone: [timeZone, [Validators.required]]
    });
  }

  editEventForm(event: Partial<Event>): FormGroup {
    const { name, description, rsvpRequired, estimatedDuration } = event;

    return this.fb.group({
      name: [
        name,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.maxLength(NAME_LENGTH),
          Validators.minLength(2)
        ]
      ],
      description: [description, [Validators.maxLength(1000)]],
      rsvpRequired: [rsvpRequired, Validators.required],
      estimatedDuration: [estimatedDuration]
    });
  }

  createCommunityForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.communityName),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      address: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        '',
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      zip: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      stateId: ['', Validators.required],
      divisionId: ['', Validators.required]
    });
  }

  editCommunityForm(community: Partial<Community>): FormGroup {
    const {
      name,
      address,
      zip,
      city,
      state: { id: stateId },
      division: { id: divisionId }
    } = community;

    return this.fb.group({
      name: [
        name,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.communityName),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      address: [
        address,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        city,
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      zip: [zip, [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      stateId: [stateId, Validators.required],
      divisionId: [divisionId, Validators.required]
    });
  }

  createCommunitiesFiltersForm(): FormGroup {
    return this.fb.group({
      divisionsIds: [null],
      communitiesIds: [null],
      city: [null],
      statesIds: [null]
    });
  }

  createCommunityContactsFiltersForm(): FormGroup {
    return this.fb.group({
      contactTypeIds: [null],
      affiliation: [null],
      companyIds: [null],
      name: [null],
      jobTitle: [null],
      email: [null]
    });
  }

  createSignInForm(): FormGroup {
    return this.fb.group({
      email: [
        '',
        Validators.compose([Validators.required, this.formValidationService.isValidEmail])
      ],
      password: ['', Validators.compose([Validators.required])]
    });
  }

  createForgotPasswordForm(): FormGroup {
    return this.fb.group({
      email: [
        null,
        Validators.compose([this.formValidationService.isValidEmail, Validators.required])
      ]
    });
  }

  createResetPasswordForm({ email, resetCode }: ResetPasswordInitData): FormGroup {
    return this.fb.group({
      email: [email, [this.formValidationService.isValidEmail, Validators.required]],
      resetCode: [resetCode, Validators.required],
      newPassword: [null, [Validators.minLength(8), Validators.required]]
    });
  }

  // Commented code needs for when Contacts will be brought back to the original logic and flow
  createContactForm(): FormGroup {
    const disabledInitialValue = { value: '', disabled: true };

    return this.fb.group({
      // companyType: ContactCompanyType.Staff,
      email: ['', [Validators.required, this.formValidationService.isValidEmail]],
      // jobTitle: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.jobTitle)]],
      // phone: ['', [Validators.required, this.formValidationService.isValidPhone]],
      // workPhone: ['', [this.formValidationService.isValidPhone, Validators.required]],
      // displayMobilePhone: [true],
      typeId: ['', Validators.required],
      description: [
        '',
        [Validators.required, Validators.maxLength(VALIDATION_CONFIGS.stringMaxLength)]
      ],
      // firstName: [
      //   '',
      //   [
      //     Validators.required,
      //     Validators.pattern(VALIDATION_PATTERNS.personName),
      //     Validators.maxLength(NAME_LENGTH),
      //     Validators.minLength(2)
      //   ]
      // ],
      // lastName: [
      //   '',
      //   [
      //     Validators.required,
      //     Validators.pattern(VALIDATION_PATTERNS.personName),
      //     Validators.maxLength(NAME_LENGTH),
      //     Validators.minLength(2)
      //   ]
      // ],
      // isGroupContact: [true],
      groupName: [
        '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(NAME_LENGTH)]
      ],
      groupMembersNames: ['', Validators.required]

      // conditional fields
      // companyId: [disabledInitialValue, Validators.required],
      // userId: ['', Validators.required],
      // companyName: [
      //   disabledInitialValue,
      //   [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_LENGTH)]
      // ],
      // companyStateId: [disabledInitialValue, Validators.required]
    });
  }

  // Commented code needs for when Contacts will be brought back to the original logic and flow
  editContactForm(contact: Partial<Contact>): FormGroup {
    const disabledInitialValue = { value: '', disabled: true };
    const isVendor = contact.affiliation === ContactCompanyType.Vendor;
    const groupMembersNames = contact.groupMembersNames
      ? parseGroupMembersNamesToInput(contact.groupMembersNames)
      : null;

    return this.fb.group({
      // companyType: contact.affiliation,
      email: [contact.email, [Validators.required, this.formValidationService.isValidEmail]],
      // jobTitle: [
      //   contact.title,
      //   [Validators.required, Validators.pattern(VALIDATION_PATTERNS.jobTitle)]
      // ],
      // phone: [contact.mobilePhone, [Validators.required, this.formValidationService.isValidPhone]],
      // workPhone: [
      //   contact.workPhone,
      //   [this.formValidationService.isValidPhone, Validators.required]
      // ],
      // displayMobilePhone: [contact.displayMobilePhone],
      typeId: [contact.typeId, Validators.required],
      description: [
        contact.description,
        [Validators.required, Validators.maxLength(VALIDATION_CONFIGS.stringMaxLength)]
      ],
      // firstName: [
      //   contact.firstName,
      //   [
      //     Validators.required,
      //     Validators.pattern(VALIDATION_PATTERNS.personName),
      //     Validators.maxLength(NAME_LENGTH),
      //     Validators.minLength(2)
      //   ]
      // ],
      // lastName: [
      //   contact.lastName,
      //   [
      //     Validators.required,
      //     Validators.pattern(VALIDATION_PATTERNS.personName),
      //     Validators.maxLength(NAME_LENGTH),
      //     Validators.minLength(2)
      //   ]
      // ],
      // isGroupContact: [true],
      groupName: [
        contact.groupName,
        [Validators.required, Validators.minLength(2), Validators.maxLength(NAME_LENGTH)]
      ],
      groupMembersNames: [groupMembersNames, Validators.required]
      // userId: [{ value: contact.user?.id, disabled: isVendor }],
      // companyId: [{ value: contact.company?.id, disabled: !isVendor }, Validators.required],
      // companyName: [
      //   disabledInitialValue,
      //   [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(NAME_LENGTH)]
      // ],
      // companyStateId: [disabledInitialValue, Validators.required]
    });
  }

  createContactsFiltersForm(): FormGroup {
    return this.fb.group({
      divisionsIds: [null],
      communitiesIds: [null],
      contactTypesIds: [null],
      affiliation: [null],
      companiesIds: [null],
      name: [null],
      jobTitle: [null],
      email: [null]
    });
  }

  createContactTypeForm(): FormGroup {
    return this.fb.group({
      label: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH),
          Validators.pattern(VALIDATION_PATTERNS.entityName)
        ]
      ],
      affiliation: [ContactCompanyType.Staff, [Validators.required]],
      description: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(VALIDATION_CONFIGS.stringMaxLength)
        ]
      ]
    });
  }

  updateContactTypeForm(teamMemberType: Partial<TeamMemberType>): FormGroup {
    return this.fb.group({
      label: [
        teamMemberType.label,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH),
          Validators.pattern(VALIDATION_PATTERNS.entityName)
        ]
      ],
      affiliation: [teamMemberType.affiliation, [Validators.required]],
      description: [
        teamMemberType.description,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(VALIDATION_CONFIGS.stringMaxLength)
        ]
      ]
    });
  }

  createContactVendorForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.entityName),
          Validators.maxLength(NAME_LENGTH),
          Validators.minLength(2)
        ]
      ],
      stateId: ['', [Validators.required]]
    });
  }

  updateContactsVendorsForm(vendor: Partial<Company>): FormGroup {
    return this.fb.group({
      name: [
        vendor.name,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.entityName),
          Validators.maxLength(NAME_LENGTH),
          Validators.minLength(2)
        ]
      ],
      stateId: [vendor.stateId, [Validators.required]]
    });
  }

  createContactTypesFiltersForm(): FormGroup {
    return this.fb.group({
      typesNames: [null],
      affiliation: [null],
      description: [null]
    });
  }

  createContactVendorsFiltersForm(): FormGroup {
    return this.fb.group({
      names: [null],
      stateIds: [null]
    });
  }

  createPhaseForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      description: ['', [Validators.required, Validators.maxLength(1000)]],
      videoUrl: ['', Validators.pattern(VALIDATION_PATTERNS.url)],
      videoPreviewImage: ''
    });
  }

  editPhaseForm(phase: Partial<DefaultPhase | Phase>): FormGroup {
    return this.fb.group({
      name: [
        phase.name,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      description: [phase.description, [Validators.required, Validators.maxLength(1000)]],
      videoUrl: [phase.videoUrl, Validators.pattern(VALIDATION_PATTERNS.url)],
      videoPreviewImage: phase.videoPreviewImage
    });
  }

  createPhaseTipForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      description: ['', [Validators.required, Validators.maxLength(1000)]]
    });
  }

  editPhaseTipForm(phaseTip: Partial<PhaseTip> | Partial<DefaultPhaseTip>): FormGroup {
    return this.fb.group({
      name: [
        phaseTip.name,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      description: [phaseTip.description, [Validators.required, Validators.maxLength(1000)]]
    });
  }

  createPhaseTipsFiltersForm(): FormGroup {
    return this.fb.group({
      name: [null],
      description: [null]
    });
  }

  createPlanForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ]
    });
  }

  updatePlanForm(planName: string): FormGroup {
    return this.fb.group({
      name: [
        planName,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ]
    });
  }

  createElevationForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ]
    });
  }

  updateElevationForm(elevationName: string): FormGroup {
    return this.fb.group({
      name: [
        elevationName,
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ]
    });
  }

  createPlanFiltersForm(): FormGroup {
    return this.fb.group({
      divisionIds: [null],
      communityIds: [null],
      name: [null],
      hasElevations: [null]
    });
  }

  createHomeForm(): FormGroup {
    return this.fb.group({
      divisionId: ['', [Validators.required]],
      communityId: ['', [Validators.required]],
      planId: ['', [Validators.required]],
      elevationId: ['', [Validators.required]],
      elevationRendering: [''],
      homesite: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.homesite)]],
      address: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        '',
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      zip: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      stateId: ['', Validators.required],
      buyerFirstName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      buyerLastName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      buyerEmail: ['', [Validators.required, this.formValidationService.isValidEmail]],
      buyerPhone: ['', [Validators.required, this.formValidationService.isValidPhone]],
      /*coBuyerFirstName: [''],
      coBuyerLastName: [''],*/
      contractDate: ['', [Validators.required]],
      phaseId: ['', [Validators.required]],
      estimatedDeliveryDate: [''],
      closingDate: ['']
    });
  }

  editHomeForm(): FormGroup {
    return this.fb.group({
      divisionId: ['', [Validators.required]],
      communityId: ['', [Validators.required]],
      planId: ['', [Validators.required]],
      elevationId: ['', [Validators.required]],
      elevationRendering: [''],
      homesite: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.homesite)]],
      address: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.address),
          Validators.minLength(2)
        ]
      ],
      city: [
        '',
        [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city), Validators.minLength(2)]
      ],
      zip: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
      stateId: ['', Validators.required],
      buyerFirstName: ['', Validators.maxLength(NAME_LENGTH)],
      buyerLastName: ['', Validators.maxLength(NAME_LENGTH)],
      buyerEmail: [''],
      buyerPhone: [''],
      contractDate: ['', [Validators.required]],
      phaseId: ['', [Validators.required]],
      estimatedDeliveryDate: [''],
      closingDate: ['']
    });
  }

  createHomesFiltersForm(isArchived: boolean): FormGroup {
    return this.fb.group({
      contractDateFrom: [null],
      contractDateTo: [null],
      divisionsIds: [null],
      communitiesIds: [null],
      planName: [null],
      address: [null],
      homesite: [null],
      buyerNames: [null],
      phasesNames: [null],
      ...(isArchived
        ? { contractCancelledDateFrom: [null], contractCancelledDateTo: [null] }
        : {
            lastLoginDateFrom: [null],
            lastLoginDateTo: [null]
          })
    });
  }

  createLoginForm(): FormGroup {
    return this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      email: ['', [Validators.required, this.formValidationService.isValidEmail]],
      phone: ['', [this.formValidationService.isValidPhone]],
      relationship: ['', [Validators.required]],
      activated: [false],
      resetPassword: [false]
    });
  }

  editLoginForm(login: Partial<Buyer>): FormGroup {
    return this.fb.group({
      firstName: [
        login.user.firstName,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      lastName: [
        login.user.lastName,
        [
          Validators.required,
          Validators.pattern(VALIDATION_PATTERNS.personName),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      email: [login.user.email, [Validators.required, this.formValidationService.isValidEmail]],
      phone: [login.user.phone, [Validators.required, this.formValidationService.isValidPhone]],
      relationship: [login.relationship, [Validators.required]],
      activated: [login.activated],
      resetPassword: [false]
    });
  }

  communitiesWithPhasesSubForm(communitiesIds: number[] = [], phasesIds: number[] = []): FormGroup {
    return this.fb.group({
      communitiesIds: [communitiesIds, Validators.required],
      phasesIds: [phasesIds, Validators.required]
    });
  }

  createHomeSelectionForm(): FormGroup {
    return this.fb.group({
      divisionId: [null],
      communityId: [null],
      lotId: [null]
    });
  }

  createEvent(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(NAME_PATTERN),
          Validators.minLength(2),
          Validators.maxLength(NAME_LENGTH)
        ]
      ],
      description: ['', [Validators.maxLength(1000)]],
      rsvpRequired: [false],
      estimatedDuration: [null]
    });
  }

  createHomeEventForm(): FormGroup {
    return this.fb.group(
      {
        eventId: ['', Validators.required],
        name: [
          '',
          [
            Validators.required,
            Validators.pattern(NAME_PATTERN),
            Validators.minLength(2),
            Validators.maxLength(NAME_LENGTH)
          ]
        ],
        phaseId: ['', Validators.required],
        description: ['', [Validators.maxLength(1000)]],
        date: ['', Validators.required],
        start: ['', Validators.required],
        end: ['', Validators.required],
        invitee: ['', [Validators.required, this.formValidationService.isValidInviteesGroup]],
        rsvpRequired: [false, Validators.required],
        address: [
          '',
          [
            Validators.required,
            Validators.pattern(VALIDATION_PATTERNS.address),
            Validators.minLength(2)
          ]
        ],
        address2: ['', [Validators.pattern(VALIDATION_PATTERNS.address), Validators.minLength(2)]],
        city: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city)]],
        stateId: ['', Validators.required],
        zip: ['', [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]],
        requestedBy: ['', Validators.required]
      },
      { validators: this.formValidationService.isValidTimeRange }
    );
  }

  editHomeEventForm(lotAppointment: LotAppointment): FormGroup {
    const eventBuyers = lotAppointment.lotAppointmentBuyers.map(dude => dude.buyer);
    const eventContacts = lotAppointment.lotAppointmentTeamMembers.map(dude => dude.teamMember);

    const invitees = mapInviteesToDropdown([...eventBuyers, ...eventContacts]);

    return this.fb.group(
      {
        name: [
          lotAppointment.name,
          [
            Validators.required,
            Validators.pattern(NAME_PATTERN),
            Validators.minLength(2),
            Validators.maxLength(NAME_LENGTH)
          ]
        ],
        phaseId: [lotAppointment.phaseId, Validators.required],
        description: [lotAppointment.description],
        date: [lotAppointment.start, Validators.required],
        start: [lotAppointment.start, Validators.required],
        end: [lotAppointment.end, Validators.required],
        invitee: [invitees, [Validators.required, this.formValidationService.isValidInviteesGroup]],
        rsvpRequired: [lotAppointment.rsvpRequired, Validators.required],
        address: [
          lotAppointment.address,
          [Validators.required, Validators.pattern(VALIDATION_PATTERNS.address)]
        ],
        address2: [lotAppointment.address2, Validators.pattern(VALIDATION_PATTERNS.address)],
        city: [
          lotAppointment.city,
          [Validators.required, Validators.pattern(VALIDATION_PATTERNS.city)]
        ],
        stateId: [lotAppointment.stateId, Validators.required],
        zip: [
          lotAppointment.zip,
          [Validators.required, Validators.pattern(VALIDATION_PATTERNS.usaZipCode)]
        ],
        requestedBy: [lotAppointment.requestedBy]
      },
      { validators: this.formValidationService.isValidTimeRange }
    );
  }

  rescheduleHomeEventForm(): FormGroup {
    return this.fb.group(
      {
        date: ['', Validators.required],
        start: ['', Validators.required],
        end: ['', Validators.required]
      },
      { validators: this.formValidationService.isValidTimeRange }
    );
  }

  createDocumentsFiltersForm(): FormGroup {
    return this.fb.group({
      divisionIds: [null],
      communityIds: [null],
      name: [null],
      phaseNames: [null],
      description: [null]
    });
  }

  createDocument(): FormGroup {
    return this.fb.group({
      fileName: [null, [Validators.required]],
      fileUrl: [null, Validators.required],
      fileSize: [null, Validators.required],
      name: [
        null,
        [Validators.required, Validators.maxLength(NAME_LENGTH), Validators.minLength(2)]
      ],
      description: [
        null,
        [Validators.required, Validators.maxLength(1000), Validators.minLength(2)]
      ]
    });
  }

  editDocument(document: Partial<Document>): FormGroup {
    const { fileName, fileUrl, fileSize, name, description } = document;

    return this.fb.group({
      fileName: [fileName, [Validators.required, Validators.minLength(2)]],
      fileUrl: [fileUrl, Validators.required],
      fileSize: [fileSize, Validators.required],
      name: [
        name,
        [Validators.required, Validators.maxLength(NAME_LENGTH), Validators.minLength(2)]
      ],
      description: [
        description,
        [Validators.required, Validators.maxLength(1000), Validators.minLength(2)]
      ]
    });
  }

  createChatsFiltersForm(): FormGroup {
    return this.fb.group({
      start: [null],
      end: [null],
      divisionIds: [null],
      communityIds: [null],
      address: [null],
      contactName: [null],
      buyerName: [null]
    });
  }

  createHomeDocument(): FormGroup {
    return this.fb.group({
      fileName: [null, Validators.required],
      fileUrl: [null, Validators.required],
      fileSize: [null, Validators.required],
      name: [
        null,
        [Validators.required, Validators.maxLength(NAME_LENGTH), Validators.minLength(2)]
      ],
      description: [
        null,
        [Validators.required, Validators.minLength(2), Validators.maxLength(1000)]
      ],
      phasesIds: [null, [Validators.required]]
    });
  }

  editHomeDocument(lotDocument: LotDocument): FormGroup {
    const { fileName, fileUrl, fileSize, name, description, phaseAssignments } = lotDocument;
    const phasesIds = phaseAssignments.map(phaseAssignment => phaseAssignment.phase.id);

    return this.fb.group({
      fileName: [fileName, [Validators.required, Validators.minLength(2)]],
      fileUrl: [fileUrl, Validators.required],
      fileSize: [fileSize, Validators.required],
      name: [name, [Validators.required, Validators.maxLength(NAME_LENGTH)]],
      description: [
        description,
        [Validators.required, Validators.minLength(2), Validators.maxLength(1000)]
      ],
      phasesIds: [phasesIds, [Validators.required]]
    });
  }

  createHomeScheduleFilterForm(): FormGroup {
    return this.fb.group({
      start: [null],
      end: [null],
      phaseNames: [null],
      attendees: [null],
      statuses: [null],
      name: [null]
    });
  }

  createHomeDocumentsFiltersForm(): FormGroup {
    return this.fb.group({
      name: [null],
      phaseNames: [null],
      description: [null]
    });
  }

  createReportFiltersForm(): FormGroup {
    return this.fb.group({
      date: [null],
      divisionsIds: [null],
      communitiesIds: [null],
      phaseNames: [null]
    });
  }
}
